import { useRef, useEffect, useState } from 'react';
import { ResultModal } from '../../components/ResultModal';
import './JackpotWheel.css';

const DEFAULT_JACKPOT_SEGS = [
  { label: 'Nothing', pct: 0, color: '#2a2a2a', textColor: '#888888' },
  { label: '5%', pct: 5, color: '#3498DB', textColor: '#FFFFFF' },
  { label: '10%', pct: 10, color: '#2ECC71', textColor: '#FFFFFF' },
  { label: 'Nothing', pct: 0, color: '#2a2a2a', textColor: '#888888' },
  { label: '5%', pct: 5, color: '#3498DB', textColor: '#FFFFFF' },
  { label: '25%', pct: 25, color: '#9B59B6', textColor: '#FFFFFF' },
  { label: 'Nothing', pct: 0, color: '#2a2a2a', textColor: '#888888' },
  { label: '5%', pct: 5, color: '#3498DB', textColor: '#FFFFFF' },
  { label: '10%', pct: 10, color: '#2ECC71', textColor: '#FFFFFF' },
  { label: 'Nothing', pct: 0, color: '#2a2a2a', textColor: '#888888' },
  { label: '50%', pct: 50, color: '#E67E22', textColor: '#FFFFFF' },
  { label: '100%', pct: 100, color: '#FC4420', textColor: '#FFFFFF' },
];

const RAD = Math.PI / 180;

// Alternating jackpot wheel colors
const JP_COLOR_DARK = '#7b120a';
const JP_COLOR_GOLD = '#f6d56c';
function jpAltColor(i) { return i % 2 === 0 ? JP_COLOR_DARK : JP_COLOR_GOLD; }
const JP_TEXT_LIGHT = '#fef08a';
const JP_TEXT_DARK = '#450a0a';
function jpAltTextColor(i) { return i % 2 === 0 ? JP_TEXT_LIGHT : JP_TEXT_DARK; }

function normalizedPtr(rotation) {
  return ((360 - (rotation % 360)) + 360) % 360;
}

function segAtPtr(rotation, deg, num) {
  return Math.floor((normalizedPtr(rotation) + 360) % 360 / deg) % num;
}

export default function JackpotWheel({ onClose, onResult, jackpotPool = 0, tickets = 0, segments: segmentsProp }) {
  const segments = (segmentsProp || DEFAULT_JACKPOT_SEGS).filter(s => s.isVisible !== false);

  const cfgRef = useRef(null);
  cfgRef.current = {
    segments,
    num: segments.length,
    deg: 360 / segments.length,
  };

  const jackpotPoolRef = useRef(jackpotPool);
  jackpotPoolRef.current = jackpotPool;

  const canvasRef = useRef(null);
  const S = useRef({
    rotation: 0,
    isSpinning: false,
    spinStartTime: 0,
    startRotation: 0,
    totalRotation: 0,
    spinDuration: 5000,
    velocity: 0,
    winSeg: null,
    glowPhase: 0,
    pDeflect: 3.5,
    pVel: 0,
    lastSeg: 0,
  });

  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [hasSpun, setHasSpun] = useState(false);

  const audioCtxRef = useRef(null);
  const setSpinRef = useRef(setSpinning);
  setSpinRef.current = setSpinning;

  function playTick(velocity) {
    try {
      const ac = audioCtxRef.current;
      if (!ac || ac.state === 'suspended') return;
      const t = ac.currentTime;
      const buf = ac.createBuffer(1, ac.sampleRate * 0.03, ac.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++)
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 6);
      const src = ac.createBufferSource();
      src.buffer = buf;
      const f = ac.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = Math.max(800, Math.min(3200, 800 + velocity * 60));
      f.Q.value = 2.5;
      const g = ac.createGain();
      g.gain.setValueAtTime(Math.min(0.6, 0.15 + velocity * 0.012), t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
      src.connect(f); f.connect(g); g.connect(ac.destination);
      src.start(t); src.stop(t + 0.05);
    } catch (_) { }
  }

  function playWin() {
    try {
      const ac = audioCtxRef.current;
      if (!ac) return;
      if (ac.state === 'suspended') ac.resume();
      [523, 659, 784, 880, 1047, 1319].forEach((freq, i) => {
        const osc = ac.createOscillator();
        const gain = ac.createGain();
        osc.connect(gain); gain.connect(ac.destination);
        const t = ac.currentTime + i * 0.10;
        osc.type = i % 2 === 0 ? 'sine' : 'triangle';
        osc.frequency.setValueAtTime(freq, t);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.3, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
        osc.start(t); osc.stop(t + 0.32);
      });
    } catch (_) { }
  }

  function finalizeSpin() {
    const s = S.current;
    s.velocity = 0;
    s.isSpinning = false;
    const { segments: segs, deg: fDeg, num: fNum } = cfgRef.current;
    const win = segAtPtr(s.rotation, fDeg, fNum);
    s.winSeg = win;
    s.glowPhase = 0;
    const seg = segs[win];
    setSpinRef.current(false);
    const amount = seg.pct > 0 ? (jackpotPoolRef.current * seg.pct) / 100 : 0;
    const res = { label: seg.label, pct: seg.pct, amount };
    if (seg.pct > 0) playWin();
    setTimeout(() => {
      setResult(res);
      if (onResult) onResult(res);
    }, 600);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    let rafId;

    function setupCanvas() {
      const dpr = window.devicePixelRatio || 1;
      const cont = canvas.parentElement;
      const size = Math.min(cont.clientWidth, cont.clientHeight);
      canvas.style.width = size + 'px';
      canvas.style.height = size + 'px';
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      canvas.getContext('2d').scale(dpr, dpr);
    }

    function draw(customRotation) {
      const s = S.current;
      const { segments: segs, num: cNum, deg: cDeg } = cfgRef.current;
      const dpr = window.devicePixelRatio || 1;
      const W = canvas.width / dpr;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, W, W);

      const cx = W / 2, cy = W / 2;
      const outerR = W * 0.435;
      const rimW = W * 0.032;
      const segR = outerR - rimW;
      const hubR = W * 0.074;
      const rot = (customRotation ?? s.rotation) * RAD;

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.7)';
      ctx.shadowBlur = 22;
      ctx.shadowOffsetY = 5;
      ctx.beginPath();
      ctx.arc(cx, cy, outerR + 6, 0, Math.PI * 2);
      ctx.fillStyle = '#1A0C00';
      ctx.fill();
      ctx.restore();

      ctx.beginPath();
      ctx.arc(cx, cy, outerR + 3, 0, Math.PI * 2);
      ctx.fillStyle = '#C49000';
      ctx.fill();

      const rimGrad = ctx.createLinearGradient(cx - outerR, cy - outerR, cx + outerR, cy + outerR);
      rimGrad.addColorStop(0.00, '#FFF4A0');
      rimGrad.addColorStop(0.20, '#FFD700');
      rimGrad.addColorStop(0.50, '#DAA520');
      rimGrad.addColorStop(0.80, '#C49000');
      rimGrad.addColorStop(1.00, '#A07800');
      ctx.beginPath();
      ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
      ctx.fillStyle = rimGrad;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(cx, cy, segR + 3, 0, Math.PI * 2);
      ctx.lineWidth = 5; ctx.strokeStyle = '#1A0C00'; ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, segR + 1, 0, Math.PI * 2);
      ctx.lineWidth = 2; ctx.strokeStyle = '#FFD700'; ctx.stroke();

      const glowAlpha = s.winSeg !== null && !s.isSpinning
        ? 0.22 + 0.16 * Math.sin(s.glowPhase) : 0;

      for (let i = 0; i < cNum; i++) {
        const a0 = rot + i * cDeg * RAD;
        const a1 = rot + (i + 1) * cDeg * RAD;
        const mid = (a0 + a1) / 2;
        const seg = segs[i];

        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, segR, a0, a1);
        ctx.closePath();
        ctx.fillStyle = jpAltColor(i);
        ctx.fill();

        const gx = cx + Math.cos(mid) * segR;
        const gy = cy + Math.sin(mid) * segR;
        const sg = ctx.createLinearGradient(cx, cy, gx, gy);
        sg.addColorStop(0.00, 'rgba(255,255,255,0.28)');
        sg.addColorStop(0.40, 'rgba(255,255,255,0.06)');
        sg.addColorStop(1.00, 'rgba(0,0,0,0.18)');
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, segR, a0, a1);
        ctx.closePath();
        ctx.fillStyle = sg; ctx.fill();

        ctx.strokeStyle = 'rgba(0,0,0,0.14)';
        ctx.lineWidth = 0.7; ctx.stroke();

        if (i === s.winSeg && glowAlpha > 0) {
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.arc(cx, cy, segR, a0, a1);
          ctx.closePath();
          ctx.fillStyle = `rgba(255,255,180,${glowAlpha})`;
          ctx.fill();
          ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 18;
          ctx.beginPath(); ctx.moveTo(cx, cy);
          ctx.arc(cx, cy, segR, a0, a1); ctx.closePath();
          ctx.strokeStyle = 'rgba(255,215,0,0.7)';
          ctx.lineWidth = 3; ctx.stroke();
          ctx.restore();
        }

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(mid);
        const fontSize = Math.max(9, W * 0.030);
        const textR = segR * 0.91;
        ctx.font = `900 ${fontSize}px "Arial Black","Arial",sans-serif`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.strokeStyle = 'rgba(0,0,0,0.95)';
        ctx.lineWidth = Math.max(2.5, fontSize * 0.22);
        ctx.lineJoin = 'round';
        ctx.strokeText(seg.label, textR, 0);
        ctx.fillStyle = jpAltTextColor(i);
        ctx.fillText(seg.label, textR, 0);
        ctx.restore();
      }

      for (let i = 0; i < cNum; i++) {
        const angle = rot + i * cDeg * RAD;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(angle) * (hubR + 3), cy + Math.sin(angle) * (hubR + 3));
        ctx.lineTo(cx + Math.cos(angle) * segR, cy + Math.sin(angle) * segR);
        ctx.strokeStyle = 'rgba(0,0,0,0.18)'; ctx.lineWidth = 1; ctx.stroke();
      }

      const pegR = W * 0.007;
      for (let i = 0; i < cNum; i++) {
        const angle = rot + i * cDeg * RAD;
        const px = cx + Math.cos(angle) * (segR - pegR * 0.4);
        const py = cy + Math.sin(angle) * (segR - pegR * 0.4);
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 4;
        const pg = ctx.createRadialGradient(px - pegR * 0.35, py - pegR * 0.35, pegR * 0.05, px, py, pegR);
        pg.addColorStop(0, '#FFFDE0'); pg.addColorStop(0.3, '#FFD700');
        pg.addColorStop(0.7, '#DAA520'); pg.addColorStop(1, '#8B6400');
        ctx.beginPath(); ctx.arc(px, py, pegR, 0, Math.PI * 2);
        ctx.fillStyle = pg; ctx.fill();
        ctx.restore();
      }

      ctx.beginPath(); ctx.arc(cx, cy, hubR + 5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.fill();

      const hg = ctx.createRadialGradient(cx - hubR * 0.32, cy - hubR * 0.32, hubR * 0.04, cx + hubR * 0.12, cy + hubR * 0.12, hubR);
      hg.addColorStop(0.00, '#FFFFF0'); hg.addColorStop(0.22, '#FFD700');
      hg.addColorStop(0.58, '#DAA520'); hg.addColorStop(0.84, '#B8860B');
      hg.addColorStop(1.00, '#704000');
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.4)'; ctx.shadowBlur = 8;
      ctx.beginPath(); ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
      ctx.fillStyle = hg; ctx.fill();
      ctx.restore();
      ctx.beginPath(); ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(80,40,0,0.7)'; ctx.lineWidth = 2; ctx.stroke();

      ctx.beginPath();
      ctx.ellipse(cx - hubR * 0.28, cy - hubR * 0.32, hubR * 0.22, hubR * 0.14, -Math.PI / 4, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.fill();

      ctx.save();
      ctx.font = `${hubR * 0.9}px serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillText('♛', cx + 1, cy + 1);
      ctx.fillStyle = '#FFE066';
      ctx.fillText('♛', cx, cy);
      ctx.restore();

      const pointerPegR = W * 0.007;
      const baseX = cx + outerR + W * 0.008;
      const halfH = W * 0.030;
      const tipNomX = cx + (segR - pointerPegR * 2.5);
      const armLen = baseX - tipNomX;
      const rotAngle = s.pDeflect / armLen;
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 12;
      ctx.shadowOffsetX = 4; ctx.shadowOffsetY = 3;
      ctx.translate(baseX, cy); ctx.rotate(rotAngle); ctx.translate(-baseX, -cy);
      ctx.beginPath();
      ctx.moveTo(tipNomX, cy);
      ctx.lineTo(baseX, cy - halfH);
      ctx.arc(baseX, cy, halfH, -Math.PI / 2, Math.PI / 2);
      ctx.closePath();
      const pGrad = ctx.createLinearGradient(tipNomX, cy - halfH, baseX + halfH, cy + halfH);
      pGrad.addColorStop(0, '#FFFFFF'); pGrad.addColorStop(0.25, '#F4F4F4');
      pGrad.addColorStop(0.65, '#DEDEDE'); pGrad.addColorStop(1, '#C0C0C0');
      ctx.fillStyle = pGrad; ctx.fill();
      ctx.strokeStyle = '#888888'; ctx.lineWidth = 2; ctx.stroke();
      const pinR = halfH * 0.38;
      ctx.beginPath(); ctx.arc(baseX, cy, pinR, 0, Math.PI * 2);
      const pinG = ctx.createRadialGradient(baseX - pinR * 0.3, cy - pinR * 0.3, 0, baseX, cy, pinR);
      pinG.addColorStop(0, '#FFE066'); pinG.addColorStop(0.5, '#DAA520'); pinG.addColorStop(1, '#B8860B');
      ctx.fillStyle = pinG; ctx.fill();
      ctx.strokeStyle = '#8B6914'; ctx.lineWidth = 1.2; ctx.stroke();
      ctx.restore();
    }

    function loop() {
      const s = S.current;
      const { deg: lDeg, num: lNum } = cfgRef.current;
      const now = performance.now();

      if (s.isSpinning) {
        const elapsed = now - s.spinStartTime;
        const progress = Math.min(elapsed / s.spinDuration, 1);
        const remaining = Math.max(0, 1 - progress);
        const easeOut = 1 - Math.pow(remaining, 3.15);
        const nextRot = s.startRotation + s.totalRotation * easeOut;
        s.velocity = nextRot - s.rotation;
        s.rotation = nextRot;
        if (progress >= 1) finalizeSpin();

        const cur = segAtPtr(s.rotation, lDeg, lNum);
        if (cur !== s.lastSeg) {
          const speed = Math.abs(s.velocity);
          s.pDeflect = speed > 3 ? 6 + speed * 0.15 : speed > 0.5 ? 12 + (3 - speed) * 5 : 25 + (0.5 - speed) * 20;
          s.pVel = 0;
          playTick(s.velocity);
          s.lastSeg = cur;
        }
      }

      const rawAngle = ((-s.rotation % lDeg) + lDeg) % lDeg;
      const fracInSeg = rawAngle / lDeg;
      const nearestPeg = Math.min(fracInSeg, 1 - fracInSeg) * 2;
      const pegPress = (1 - nearestPeg) * 16;
      const isSettling = !s.isSpinning || Math.abs(s.velocity) < 0.3;
      s.pVel = s.pVel * (isSettling ? 0.70 : 0.62) - (isSettling ? 0.18 : 0.24) * (s.pDeflect - pegPress);
      s.pDeflect += s.pVel;

      if (s.winSeg !== null && !s.isSpinning) s.glowPhase += 0.07;

      draw(s.rotation - s.pDeflect * 0.0015);
      rafId = requestAnimationFrame(loop);
    }

    setupCanvas();
    window.addEventListener('resize', setupCanvas);
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', setupCanvas);
    };
  }, []);

  function handleSpin() {
    if (S.current.isSpinning || hasSpun) return;
    if (!audioCtxRef.current) {
      const AudioCtx = window.AudioContext || /** @type {any} */ (window).webkitAudioContext;
      audioCtxRef.current = new AudioCtx();
    }
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();

    const s = S.current;
    s.rotation = 0;
    let totalRot = 360 * (5 + Math.random() * 3);

    const { deg: hDeg, num: hNum, segments: hSegs } = cfgRef.current;

    // Avoid segments where isWin is false
    {
      const canAvoid = hSegs.some(seg => seg.isWin === false);
      const hasWinSeg = hSegs.some(seg => seg.isWin !== false);
      if (canAvoid && hasWinSeg) {
        let finalAngle = s.rotation + totalRot;
        while (hSegs[segAtPtr(finalAngle, hDeg, hNum)]?.isWin === false) {
          totalRot += hDeg;
          finalAngle = s.rotation + totalRot;
        }
      }
    }

    const finalAngle = s.rotation + totalRot;
    const segIdx = segAtPtr(finalAngle, hDeg, hNum);
    const rawAngle = ((-finalAngle % 360) + 360) % 360;
    const targetRaw = (segIdx + 0.5) * hDeg;
    let rawOffset = targetRaw - rawAngle;
    if (rawOffset > 180) rawOffset -= 360;
    if (rawOffset < -180) rawOffset += 360;
    totalRot -= rawOffset;
    if (totalRot < 360 * 5) totalRot += 360;

    s.startRotation = 0;
    s.totalRotation = totalRot;
    s.spinDuration = 5000;
    s.isSpinning = true;
    s.spinStartTime = performance.now();
    s.winSeg = null;
    s.lastSeg = segAtPtr(0, hDeg, hNum);
    setHasSpun(true);
    setSpinning(true);
    setResult(null);
  }

  const displayPool = Number(jackpotPool || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  const legendItems = (() => {
    const seen = new Set();
    return segments.reduce((acc, seg) => {
      if (!seen.has(seg.label)) {
        seen.add(seg.label);
        acc.push({ label: seg.label, color: seg.color, pct: seg.pct });
      }
      return acc;
    }, []);
  })();

  return (
    <div className="jw-overlay">
      <div className="jw-modal">
        <button
          type="button"
          className="jw-close-btn"
          onClick={onClose}
          aria-label="Close jackpot wheel"
        >
          ×
        </button>

        <div className="jw-header">
          <div className="jw-header-crown">♛</div>
          <h2 className="jw-title">JACKPOT WHEEL</h2>
          <div className="jw-pool-badge">
            <span className="jw-pool-label">Jackpot Pool</span>
            <span className="jw-pool-value">{displayPool}</span>
          </div>
        </div>

        <div className="jw-wheel-wrap">
          <canvas ref={canvasRef} className="jw-canvas" />
        </div>

        <div className="jw-legend">
          {legendItems.map(item => (
            <div key={item.label} className="jw-legend-item">
              <span className="jw-legend-dot" style={{ background: jpAltColor(legendItems.indexOf(item)) }} />
              <span className="jw-legend-text">
                {item.pct > 0 ? `${item.label} — ${item.pct}% of pool` : item.label}
              </span>
            </div>
          ))}
        </div>

        {!hasSpun && (
          <button type="button" className="jw-spin-btn" onClick={handleSpin}>
            ♛ SPIN
          </button>
        )}

        {spinning && !result && (
          <div className="jw-spinning-label">Spinning...</div>
        )}
      </div>

      {result && (
        <ResultModal
          open={Boolean(result)}
          variant="jackpot"
          title={result.pct > 0 ? (
            <>JACKPOT<br/>HIT!</>
          ) : (
            <>BETTER LUCK<br/>NEXT TIME!</>
          )}
          multiplier={result.pct > 0 ? `+$${result.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : result.label}
          subtitle={result.pct > 0 ? "Congratulations!" : 'No reward this round.'}
          subtitle2={result.pct > 0 ? `You claimed ${result.pct}% of the Jackpot Pool.` : "Try again for another chance!"}
          onClose={() => { if (tickets > 0) { setResult(null); } else { onClose(); } }}
        />
      )}
    </div>
  );
}
